import { countries } from '../mocks/countries';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const GTM_CONTAINER_ID = import.meta.env.VITE_GTM_CONTAINER_ID || '';

let initialized = false;

export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem('cookiesAccepted') === 'true';
}

export function initGTM(): void {
  if (initialized) return;
  if (!GTM_CONTAINER_ID) {
    if (import.meta.env.DEV) {
      console.warn(
        'VITE_GTM_CONTAINER_ID is empty. GTM will not be loaded. ' +
        'Copy .env.example to .env.local and fill in the value.',
      );
    }
    return;
  }
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
}

export function initGTMIfConsented(): void {
  if (hasAnalyticsConsent()) initGTM();
}

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: string;
  item_variant?: string;
  price?: number;
  currency?: string;
};

type BuildItemInput = {
  countryCode: string;
  countryName: string;
  period?: string;
  price?: number;
  currency?: string;
  productId?: number | string | null;
};

function compactPeriod(period: string): string {
  const match = period.trim().toLowerCase().match(/^(\d+)\s*(day|days|month|months|year|years)$/);
  if (!match) return period.trim().toLowerCase().replace(/\s+/g, '_');
  const [, amount, unit] = match;
  const letter = unit.startsWith('day') ? 'd' : unit.startsWith('month') ? 'm' : 'y';
  return `${amount}${letter}`;
}

export function buildEcommerceItem({ countryCode, countryName, period, price, currency, productId }: BuildItemInput): EcommerceItem {
  const item: EcommerceItem = {
    item_id: productId != null
      ? String(productId)
      : period ? `${countryCode.toLowerCase()}_${compactPeriod(period)}` : countryCode.toLowerCase(),
    item_name: period ? `${countryName} e-vignette ${period}` : `${countryName} e-vignette`,
    item_category: countryName.toLowerCase(),
  };
  if (period) item.item_variant = period.trim().toLowerCase().replace(/\s+/g, '_');
  if (price != null) item.price = price;
  if (currency) item.currency = currency;
  return item;
}

export function trackEvent(name: string, params: Record<string, unknown>): void {
  if (!hasAnalyticsConsent()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
}

export function trackEcommerceEvent(name: string, items: EcommerceItem[], extra?: Record<string, unknown>): void {
  trackEvent(name, { items, ...extra });
}

const COUNTRY_PATHS = new Set(countries.map(c => c.path));
const LEGAL_PATHS = new Set(['/terms', '/privacy', '/refund', '/cookies']);

export function getContentGroup(pathname: string): string {
  if (pathname === '/') return 'landing';
  if (COUNTRY_PATHS.has(pathname)) return 'country';
  if (pathname.startsWith('/checkout') || pathname.startsWith('/success') || pathname.startsWith('/payment')) return 'checkout';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname === '/contact') return 'contact';
  if (LEGAL_PATHS.has(pathname)) return 'legal';
  return 'other';
}

export function trackPageView(pathname: string): void {
  trackEvent('page_view', {
    page_location: window.location.href,
    page_referrer: document.referrer,
    content_group: getContentGroup(pathname),
  });
}
